<template>
  <div class="mx-auto w-full flex flex-row justify-center text-large pb-12">
    <div class="mr-16">
      <ChevronIcon
        v-if="baseChartData.length - 1 > weekIndex"
        class="cursor-pointer"
        @click="changeWeek('prev')"
      />
    </div>
    <div>{{ rangeString }}</div>
    <div class="ml-16">
      <ChevronIcon
        v-if="weekIndex > 0"
        class="rotate-180 cursor-pointer"
        @click="changeWeek('next')"
      />
    </div>
  </div>

  <div
    class="!w-full xl:!max-w-screen-xl lg:!max-w-screen-lg md:!max-w-screen-md h-96 flex justify-center items-center"
  >
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "vue-chartjs";
import "chartjs-adapter-luxon";
import { DateTime } from "luxon";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import ChevronIcon from "../icon/ChevronIcon.vue";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { IntervalType } from "../../../enum/v2/intervalType.ts";
import { ChartDataListDto } from "../../../dto/chart-data-list.dto.ts";
import { formatChallengeProgressValue } from "../../../plugins/NumberFormat";
import type { ProgressValueType } from "../../../enum/v2/progress-value-type";
dayjs.extend(weekOfYear);

const props = defineProps<{
  baseChartData: ChartDataListDto[];
  currentView: IntervalType.INTERVAL_WEEK | IntervalType.INTERVAL_MONTH;
  isPassFailChallenge?: boolean;
  progressValueType?: ProgressValueType;
}>();
const { locale, t } = useI18n();

const emptyChartData: ChartDataListDto = {
  dates: [],
  labels: [],
  values: [],
};

function getChartDataEntry(index: number): ChartDataListDto {
  const entry = props.baseChartData[index];

  // Expired challenges can return no chart buckets, so keep the chart state safe.
  if (!entry?.dates || !entry?.labels || !entry?.values) {
    return emptyChartData;
  }

  return entry;
}

const weekIndex = ref(0);
const weekChartData = ref<ChartDataListDto>(getChartDataEntry(weekIndex.value));
dayjs.extend(weekday);

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
);

watch(
  () => props.currentView,
  () => {
    weekIndex.value = 0;
    weekChartData.value = getChartDataEntry(0);
  }
);

const dates = computed(() =>
  weekChartData.value.dates.map((d: string) =>
    DateTime.fromFormat(d, "dd.MM.yyyy")
  )
);

const start = computed(() => dates.value[0]);
const end = computed(() => dates.value[dates.value.length - 1]);

const rangeString = computed(() => {
  if (dates.value.length === 0) {
    return "";
  }

  if (props.currentView === IntervalType.INTERVAL_WEEK) {
    return `${start.value?.toFormat("dd")}.–${end.value?.setLocale(locale.value).toFormat("dd")} ${end.value?.setLocale(locale.value).toFormat("LLL yy").toUpperCase()}`;
  } else if (props.currentView === IntervalType.INTERVAL_MONTH) {
    const startWeek = start.value?.weekNumber;
    const endWeek = end.value?.weekNumber;
    const startMonth = start.value
      ?.setLocale(locale.value)
      .toFormat("LLL")
      .toUpperCase();
    const endMonth = end.value
      ?.setLocale(locale.value)
      .toFormat("LLL")
      .toUpperCase();
    const startYear = start.value?.toFormat("yy");
    const endYear = end.value?.toFormat("yy");

    let monthRange =
      startMonth === endMonth && startYear === endYear
        ? `${startMonth} ${startYear}`
        : `${startMonth}-${endMonth} ${startYear === endYear ? startYear : startYear + "-" + endYear}`;
    return `${t("calendar.week.short")}${startWeek}-${endWeek}, ${monthRange}`;
  }
  return "";
});

function getCssVar(name: string) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

const highlight = getCssVar("--bkk-100");
const grey = getCssVar("--light-gray");
const todayFormatted = dayjs().format("DD.MM.YYYY");
let chartData;

if (!props.isPassFailChallenge) {
  chartData = computed(() => ({
    labels: weekChartData?.value.labels,
    datasets: [
      {
        label: "",
        data: weekChartData.value.values,
        backgroundColor: weekChartData.value.dates.map((val, index) => {
          if (props.currentView === IntervalType.INTERVAL_WEEK) {
            return weekChartData.value.dates[index] === todayFormatted
              ? highlight
              : grey;
          } else if (props.currentView === IntervalType.INTERVAL_MONTH) {
            const currentWeek = DateTime.now().weekNumber;
            const dateWeek = DateTime.fromFormat(
              weekChartData.value.dates[index],
              "dd.MM.yyyy"
            ).weekNumber;
            return dateWeek === currentWeek ? highlight : grey;
          }
          return grey;
        }),
        borderRadius: 50,
        barThickness: 40,
      },
    ],
  }));
} else {
  chartData = computed(() => ({
    labels: weekChartData.value.labels,
    datasets: [
      {
        label: "",
        data: weekChartData.value.values,
        backgroundColor: weekChartData.value.values.map((value, index) => {
          return index === 0 ? highlight : grey;
        }),
        borderRadius: 50,
        barThickness: 40,
      },
    ],
  }));
}

//https://www.chartjs.org/docs/latest/
const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      title: false,
      backgroundColor: "#ffffff",
      titleColor: "#000000",
      bodyColor: "#333333",
      borderColor: "#383838",
      borderWidth: 1,
      cornerRadius: 8,
      titleFont: {
        size: 14,
        weight: "bold",
      },
      bodyFont: {
        size: 12,
      },
      padding: 12,
      displayColors: false,
      callbacks: {
        title: () => null,
        label: (context) => formatChartValue(context.raw as number),
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      border: {
        color: "#7A7A7A",
      },
    },
    y: {
      ticks: {
        display: true,
        padding: 7,
        callback: (value) => formatChartValue(value),
        backdropPadding: 0,
        border: {
          display: false,
        },
        major: {
          enabled: true,
        },
      },
      grid: {
        color: "#7A7A7A",
        tick: {
          display: false,
        },
      },
      border: {
        display: false,
        dash: [2, 2],
      },
    },
  },
};

function formatChartValue(value: number | string): string {
  if (!props.progressValueType) {
    return String(value);
  }

  return formatChallengeProgressValue(value, props.progressValueType, {
    roundUp: true,
  });
}

function changeWeek(direction: "next" | "prev"): void {
  if (direction === "prev") {
    if (weekIndex.value < props.baseChartData.length - 1) {
      weekIndex.value++;
    }
  } else {
    if (weekIndex.value > 0) {
      weekIndex.value--;
    }
  }
  weekChartData.value = getChartDataEntry(weekIndex.value);
}

watch(
  () => props.baseChartData,
  () => {
    weekChartData.value = getChartDataEntry(weekIndex.value);
  }
);
</script>
